import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import PhoneInput, { parsePhoneNumber } from "../../common/phone-input";

const SOURCE_OPTIONS = ["RedVision", "iWell", "Bonanza"];

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_ERRORS = {
  pan: "",
  client_code: "",
  name: "",
  source: "",
  email: "",
  phone_no: "",
};

const EMPTY_FORM = {
  pan: "",
  client_code: "",
  name: "",
  country_code: "+91",
  phone_no: "",
  email: "",
  source: "",
  broker_name: "",
  broker_code: "",
  client_code_2: "",
  address_1: "",
  address_2: "",
  address_3: "",
};

const ClientFormDialog = ({ open, handleClose, onSave, editData, saving = false }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  useEffect(() => {
    if (editData) {
      // New-format clients already carry a separate country_code from the API.
      // Older clients (created before the split) still have it embedded in the
      // phone number, so fall back to parsing it out of that combined value.
      let country_code = editData.country_code;
      let phone_no = editData.phone_number || editData.phone_no || "";
      if (!country_code) {
        const parsed = parsePhoneNumber(phone_no);
        country_code = parsed.countryCode;
        phone_no = parsed.phoneNo;
      }
      setForm({
        pan:          editData.pan          || "",
        client_code:  editData.client_code  || "",
        name:         editData.client_name  || editData.name || "",
        country_code,
        phone_no,
        email:        editData.email        || "",
        source:       editData.source       || "",
        broker_name:  editData.broker_name  || "",
        broker_code:  editData.broker_code  || "",
        client_code_2: editData.client_code_2 || "",
        address_1:    editData.address_1    || "",
        address_2:    editData.address_2    || "",
        address_3:    editData.address_3    || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors(EMPTY_ERRORS);
  }, [editData, open]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in EMPTY_ERRORS) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validatePhone = (phone) => {
  const value = phone.trim();

  if (!value) return "Phone Number is required";

  if (!/^\d+$/.test(value)) {
    return "Phone number must contain digits only";
  }

  if (value.length < 6 || value.length > 15) {
    return "Please enter a valid phone number";
  }

  return "";
};

  const validate = () => {
    const pan = form.pan.trim().toUpperCase();
    const next = { ...EMPTY_ERRORS };
    if (!pan) next.pan = "PAN is required";
    else if (!PAN_REGEX.test(pan)) next.pan = "Invalid PAN format (e.g. ABCDE1234F)";
    if (!form.client_code.trim()) next.client_code = "Client Code is required";
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.source) next.source = "Source is required";
    if (form.email && !EMAIL_REGEX.test(form.email.trim())) next.email = "Invalid email address";
    next.phone_no = validatePhone(form.phone_no, form.country_code);
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const handleSave = () => {
    if (!validate()) return;
    const pan = form.pan.trim().toUpperCase();
    onSave({ ...form, pan });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editData ? "Edit Client" : "Add Client"}
        <IconButton onClick={handleClose} disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Identity
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="PAN"
              value={form.pan}
              onChange={(e) => set("pan", e.target.value.toUpperCase())}
              fullWidth
              required
              error={!!errors.pan}
              helperText={errors.pan}
              inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
            />
            <TextField
              label="Client Code"
              value={form.client_code}
              onChange={(e) => set("client_code", e.target.value)}
              fullWidth
              required
              error={!!errors.client_code}
              helperText={errors.client_code}
            />
          </Box>

          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <Box display="flex" gap={2}>
            <PhoneInput
              countryCode={form.country_code}
              phoneNo={form.phone_no}
              onCountryCodeChange={(v) => set("country_code", v)}
              onPhoneNoChange={(v) => {
                set("phone_no", v);
                setErrors((prev) => ({
                  ...prev,
                  phone_no: validatePhone(v),
                }));
              }}
              error={!!errors.phone_no}
              helperText={errors.phone_no}
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              fullWidth
              type="email"
              error={!!errors.email}
              helperText={errors.email}
            />
          </Box>

          <TextField
            select
            label="Source"
            value={form.source}
            onChange={(e) => set("source", e.target.value)}
            fullWidth
            required
            error={!!errors.source}
            helperText={errors.source}
          >
            {SOURCE_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Broker
          </Typography>

          <Box display="flex" gap={2}>
            <TextField
              label="Broker Name"
              value={form.broker_name}
              onChange={(e) => set("broker_name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Broker Code"
              value={form.broker_code}
              onChange={(e) => set("broker_code", e.target.value)}
              fullWidth
            />
          </Box>

          <TextField
            label="Client Code 2"
            value={form.client_code_2}
            onChange={(e) => set("client_code_2", e.target.value)}
            fullWidth
          />

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: -1 }}>
            Address
          </Typography>

          <TextField
            label="Address Line 1"
            value={form.address_1}
            onChange={(e) => set("address_1", e.target.value)}
            fullWidth
          />
          <TextField
            label="Address Line 2"
            value={form.address_2}
            onChange={(e) => set("address_2", e.target.value)}
            fullWidth
          />
          <TextField
            label="City / State"
            value={form.address_3}
            onChange={(e) => set("address_3", e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={handleClose} disabled={saving}>Cancel</ButtonTwo>
        <ButtonOne onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : editData ? "Update" : "Add"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFormDialog;
